import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiAlertCircle, FiCheckCircle, FiClock, FiActivity, FiBarChart2, FiUsers, FiFileText, FiShield, FiRefreshCw } from 'react-icons/fi';
import { getDashboardMetrics } from '../services/dashboardService';
import MmsDashboardRendererWrapper from '../components/common/MmsDashboardWrapper';

// Dashboard Container and Layout Components
const DashboardContainer = styled.div`
  padding: 1.5rem;
  height: 100%;
  background: #f8fafc;
  color: #1e293b;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  overflow-y: auto;
`;

const SectionTitle = styled.h2`
  font-size: 1.2rem;
  font-weight: 600;
  color: #334155;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: 'Orbitron', sans-serif;
`;

const HeaderSection = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 0.5rem;
`;

const ActionablesSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
`;

const InfoSection = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1.5rem;
`;

// Card Components
const Card = styled(motion.div)`
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  height: ${props => props.height || 'auto'};
`;

const HeaderCard = styled(Card)`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 1rem;
  gap: 1rem;
`;

const IconContainer = styled.div`
  width: 3rem;
  height: 3rem;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.bg || '#e2e8f0'};
  color: ${props => props.color || '#64748b'};
  font-size: 1.5rem;
`;

const MetricContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const MetricValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
`;

const MetricLabel = styled.div`
  font-size: 0.875rem;
  color: #64748b;
`;

const MetricChange = styled.div`
  font-size: 0.75rem;
  font-weight: 500;
  color: ${props => props.positive ? '#10b981' : '#ef4444'};
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

// List Components
const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  overflow-y: auto;
  max-height: ${props => props.maxHeight || 'none'};
`;

const ListItem = styled.div`
  padding: 0.75rem;
  border-radius: 0.5rem;
  background: ${props => props.highlighted ? '#f1f5f9' : 'transparent'};
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f1f5f9;
  }
`;

const ListItemIcon = styled.div`
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.bg || '#e2e8f0'};
  color: ${props => props.color || '#64748b'};
  font-size: 1rem;
`;

const ListItemContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const ListItemTitle = styled.div`
  font-weight: 500;
  color: #334155;
`;

const ListItemDescription = styled.div`
  font-size: 0.875rem;
  color: #64748b;
`;

const ListItemMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
`;

const ListItemTime = styled.div`
  font-size: 0.75rem;
  color: #94a3b8;
`;

const ListItemStatus = styled.div`
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.25rem 0.5rem;
  border-radius: 1rem;
  background: ${props => {
    switch (props.status) {
      case 'critical': return '#fee2e2';
      case 'warning': return '#fef3c7';
      case 'success': return '#dcfce7';
      default: return '#e2e8f0';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'critical': return '#ef4444';
      case 'warning': return '#f59e0b';
      case 'success': return '#10b981';
      default: return '#64748b';
    }
  }};
`;

// Chart Components
const ChartContainer = styled.div`
  height: 200px;
  margin-top: 1rem;
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e2e8f0;
`;

const ChartBar = styled.div`
  flex: 1;
  height: ${props => props.height}%;
  background: ${props => props.color || '#3b82f6'};
  border-radius: 0.25rem 0.25rem 0 0;
  transition: height 0.5s ease;
  position: relative;
  min-width: 20px;
  
  &:hover::after {
    content: '${props => props.value}';
    position: absolute;
    top: -25px;
    left: 50%;
    transform: translateX(-50%);
    background: #1e293b;
    color: white;
    padding: 0.25rem 0.5rem;
    border-radius: 0.25rem;
    font-size: 0.75rem;
  }
`;

const ChartLegend = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 0.5rem;
  font-size: 0.75rem;
  color: #64748b;
`;

// Progress Components
const ProgressContainer = styled.div`
  width: 100%;
  height: 0.5rem;
  background: #e2e8f0;
  border-radius: 1rem;
  overflow: hidden;
  margin: 0.5rem 0;
`;

const ProgressBar = styled.div`
  height: 100%;
  width: ${props => props.progress}%;
  background: ${props => props.color || '#3b82f6'};
  border-radius: 1rem;
  transition: width 0.5s ease;
`;

// Dashboard Component
const Dashboard = () => {
  // State for real-time data
  const [headerMetrics, setHeaderMetrics] = useState({
    activeAgents: { value: 12, change: 2, isPositive: true },
    pendingTasks: { value: 28, change: 5, isPositive: false },
    processingRate: { value: '98.2%', change: 1.5, isPositive: true },
    systemHealth: { value: '94%', change: 2.1, isPositive: true }
  });

  const [actionableTasks, setActionableTasks] = useState([
    {
      id: 1,
      title: 'Review Flagged Claims',
      description: '5 claims flagged by fraud detection require review',
      time: '10 min ago',
      status: 'critical',
      icon: <FiAlertCircle />,
      iconBg: '#fee2e2',
      iconColor: '#ef4444'
    },
    {
      id: 2,
      title: 'Document Extraction Errors',
      description: '3 documents failed automatic extraction',
      time: '25 min ago',
      status: 'warning',
      icon: <FiFileText />,
      iconBg: '#fef3c7',
      iconColor: '#f59e0b'
    },
    {
      id: 3,
      title: 'Agent Performance Review',
      description: 'Weekly performance metrics ready for review',
      time: '1 hour ago',
      status: 'normal',
      icon: <FiUsers />,
      iconBg: '#e0f2fe',
      iconColor: '#0ea5e9'
    }
  ]);

  const [alerts, setAlerts] = useState([
    {
      id: 1,
      title: 'Security Alert',
      description: 'Unusual access pattern detected in claims database',
      time: '5 min ago',
      status: 'warning',
      icon: <FiShield />,
      iconBg: '#fef3c7',
      iconColor: '#f59e0b'
    },
    {
      id: 2,
      title: 'Model Drift Detected',
      description: 'Fraud detection model accuracy dropped by 3%',
      time: '30 min ago',
      status: 'warning',
      icon: <FiActivity />,
      iconBg: '#fef3c7',
      iconColor: '#f59e0b'
    }
  ]);

  const [recentActivities, setRecentActivities] = useState([
    {
      id: 1,
      title: 'Claims Processing',
      description: 'Agent completed review of claim #45892',
      time: '2 min ago',
      status: 'success',
      icon: <FiCheckCircle />,
      iconBg: '#dcfce7',
      iconColor: '#10b981'
    },
    {
      id: 2,
      title: 'Document Extraction',
      description: 'Extracted data from 24 new documents',
      time: '15 min ago',
      status: 'success',
      icon: <FiFileText />,
      iconBg: '#dcfce7',
      iconColor: '#10b981'
    },
    {
      id: 3,
      title: 'Underwriting',
      description: 'Policy #12783 flagged for manual review',
      time: '45 min ago',
      status: 'normal',
      icon: <FiClock />,
      iconBg: '#e0f2fe',
      iconColor: '#0ea5e9'
    },
    {
      id: 4,
      title: 'Customer Service',
      description: 'Agent resolved 15 queries with 98% satisfaction',
      time: '1 hour ago',
      status: 'success',
      icon: <FiUsers />,
      iconBg: '#dcfce7',
      iconColor: '#10b981'
    }
  ]);

  const [systemPerformance, setSystemPerformance] = useState({
    claimsProcessed: [65, 72, 81, 78, 74, 85, 90],
    documentExtraction: [82, 85, 88, 84, 90, 92, 95],
    fraudDetection: [92, 90, 88, 94, 95, 93, 97],
    days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
  });
  const dashboardData={
    filtersData: [],
    graphsList: [
      {
        graphType: "bar-cluster",
        colSpan: "half",
        xAxisTitle: "Rule Category",
        yAxisTitle: "Decision(in)",
        graphData: [
          {
            "ruleCategory": "POLICY_AND_PRODUCT_DIMENSIONS",
            "ruleSubTypeCd": "LINE_OF_BUSINESS",
            "ruleName": "Line of Business",
            "weight": 5,
            "decisionValue": 2,
            "decision": 10,
            "decisionTotal": 50,
            "recommendationCategory": "MESSAGE",
            "recommendation": "Health (IPD/OPD) product identified",
            "remarks": "Product Line of Business - Health"
          },
          {
            "ruleCategory": "POLICY_AND_PRODUCT_DIMENSIONS",
            "ruleSubTypeCd": "POLICY_TYPE",
            "ruleName": "Policy Type",
            "weight": 5,
            "decisionValue": 1,
            "decision": 5,
            "decisionTotal": 50,
            "recommendationCategory": "MESSAGE",
            "recommendation": "Individual policy",
            "remarks": "Policy Type Individual"
          },
          {
            "ruleCategory": "POLICY_AND_PRODUCT_DIMENSIONS",
            "ruleSubTypeCd": "BENEFIT_AMOUNT",
            "ruleName": "Benefit Amount",
            "weight": 5,
            "decisionValue": 2,
            "decision": 10,
            "decisionTotal": 50,
            "recommendationCategory": "ADDITIONAL REQUIREMENT",
            "recommendation": "Submit income proof for high SA claim",
            "remarks": "Amount exceeds standard limits for this product, recommend income proof submission"
          },
          {
            "ruleCategory": "POLICY_AND_PRODUCT_DIMENSIONS",
            "ruleSubTypeCd": "WAITING_PERIOD",
            "ruleName": "Waiting Period",
            "weight": 5,
            "decisionValue": 2,
            "decision": 10,
            "decisionTotal": 50,
            "recommendationCategory": "MESSAGE",
            "recommendation": "Waiting period satisfied",
            "remarks": "Within waiting period limits"
          }
        ],
        graphOptions: {
          showTitle: false,
          XAxis:"ruleName",
          aggregation:"sum",
          ValueAxis:[
            {label:"Decision",value:"decision"},
            {label:"Decision Value",value:"decisionValue"},
          ]
        }
      }
    ]
  }
  const [componentStatus, setComponentStatus] = useState([
    { name: 'Claims Processing', status: 'operational', load: 78 },
    { name: 'Document Extraction', status: 'operational', load: 65 },
    { name: 'Fraud Detection', status: 'operational', load: 82 },
    { name: 'Underwriting', status: 'degraded', load: 91 },
    { name: 'Customer Service', status: 'operational', load: 45 }
  ]);

  // Simulate real-time updates
  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        // const data = await getDashboardMetrics(1735616359,1742528420); // Call the API function
        // setHeaderMetrics(data.respose);

      } catch (error) {
        console.error("Error fetching dashboard metrics:", error);
      }
    }

    fetchMetrics();

  }, []);

  return (
    <DashboardContainer>
      {/* Header Highlights */}
      <div>
        <SectionTitle><FiBarChart2 /> Real-time Metrics</SectionTitle>
        <HeaderSection>
          <HeaderCard
            whileHover={{ y: -5, boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}
            transition={{ type: 'spring', stiffness: 300 }}
          >
            <IconContainer bg="#e0f2fe" color="#0ea5e9">
              <FiUsers />
            </IconContainer>
            <MetricContainer>
              <MetricValue>{headerMetrics.activeAgents.value}</MetricValue>
              <MetricLabel>Active Agents</MetricLabel>
              <MetricChange positive={headerMetrics.activeAgents.isPositive}>
                {headerMetrics.activeAgents.isPositive ? '↑' : '↓'} {Math.abs(headerMetrics.activeAgents.change).toFixed(1)}%
              </MetricChange>
            </MetricContainer>
          </HeaderCard>

          <HeaderCard
            whileHover={{ y: -5, boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}
            transition={{ type: 'spring', stiffness: 300 }}
          >
            <IconContainer bg="#fef3c7" color="#f59e0b">
              <FiClock />
            </IconContainer>
            <MetricContainer>
              <MetricValue>{headerMetrics.pendingTasks.value}</MetricValue>
              <MetricLabel>Pending Tasks</MetricLabel>
              <MetricChange positive={headerMetrics.pendingTasks.isPositive}>
                {headerMetrics.pendingTasks.isPositive ? '↑' : '↓'} {Math.abs(headerMetrics.pendingTasks.change).toFixed(1)}%
              </MetricChange>
            </MetricContainer>
          </HeaderCard>

          <HeaderCard
            whileHover={{ y: -5, boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}
            transition={{ type: 'spring', stiffness: 300 }}
          >
            <IconContainer bg="#dcfce7" color="#10b981">
              <FiActivity />
            </IconContainer>
            <MetricContainer>
              <MetricValue>{headerMetrics.processingRate.value}</MetricValue>
              <MetricLabel>Processing Rate</MetricLabel>
              <MetricChange positive={headerMetrics.processingRate.isPositive}>
                {headerMetrics.processingRate.isPositive ? '↑' : '↓'} {Math.abs(headerMetrics.processingRate.change).toFixed(1)}%
              </MetricChange>
            </MetricContainer>
          </HeaderCard>

          <HeaderCard
            whileHover={{ y: -5, boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}
            transition={{ type: 'spring', stiffness: 300 }}
          >
            <IconContainer bg="#e0f2fe" color="#0ea5e9">
              <FiRefreshCw />
            </IconContainer>
            <MetricContainer>
              <MetricValue>{headerMetrics.systemHealth.value}</MetricValue>
              <MetricLabel>System Health</MetricLabel>
              <MetricChange positive={headerMetrics.systemHealth.isPositive}>
                {headerMetrics.systemHealth.isPositive ? '↑' : '↓'} {Math.abs(headerMetrics.systemHealth.change).toFixed(1)}%
              </MetricChange>
            </MetricContainer>
          </HeaderCard>
        </HeaderSection>
      </div>

      {/* Immediate Actionables */}
      <div>
        <SectionTitle><FiAlertCircle /> Immediate Actionables</SectionTitle>
        <ActionablesSection>
          <Card height="320px">
            <SectionTitle>Critical Tasks</SectionTitle>
            <List maxHeight="250px">
              <AnimatePresence>
                {actionableTasks.map(task => (
                  <motion.div
                    key={task.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ListItem highlighted={task.status === 'critical'}>
                      <ListItemIcon bg={task.iconBg} color={task.iconColor}>
                        {task.icon}
                      </ListItemIcon>
                      <ListItemContent>
                        <ListItemTitle>{task.title}</ListItemTitle>
                        <ListItemDescription>{task.description}</ListItemDescription>
                      </ListItemContent>
                      <ListItemMeta>
                        <ListItemTime>{task.time}</ListItemTime>
                        <ListItemStatus status={task.status}>
                          {task.status === 'critical' ? 'Critical' : task.status === 'warning' ? 'Warning' : 'Normal'}
                        </ListItemStatus>
                      </ListItemMeta>
                    </ListItem>
                  </motion.div>
                ))}
              </AnimatePresence>
            </List>
          </Card>

          <Card height="320px">
            <SectionTitle>System Alerts</SectionTitle>
            <List maxHeight="250px">
              <AnimatePresence>
                {alerts.map(alert => (
                  <motion.div
                    key={alert.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ListItem highlighted={alert.status === 'critical'}>
                      <ListItemIcon bg={alert.iconBg} color={alert.iconColor}>
                        {alert.icon}
                      </ListItemIcon>
                      <ListItemContent>
                        <ListItemTitle>{alert.title}</ListItemTitle>
                        <ListItemDescription>{alert.description}</ListItemDescription>
                      </ListItemContent>
                      <ListItemMeta>
                        <ListItemTime>{alert.time}</ListItemTime>
                        <ListItemStatus status={alert.status}>
                          {alert.status === 'critical' ? 'Critical' : alert.status === 'warning' ? 'Warning' : 'Normal'}
                        </ListItemStatus>
                      </ListItemMeta>
                    </ListItem>
                  </motion.div>
                ))}
              </AnimatePresence>
            </List>
          </Card>
        </ActionablesSection>
      </div>

      {/* Informational Section */}
      <div>
        <SectionTitle><FiActivity /> System Activity</SectionTitle>
        <InfoSection>
          <Card>
            <SectionTitle>Recent Activities</SectionTitle>
            <List maxHeight="300px">
              <AnimatePresence>
                {recentActivities.map(activity => (
                  <motion.div
                    key={activity.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ListItem>
                      <ListItemIcon bg={activity.iconBg} color={activity.iconColor}>
                        {activity.icon}
                      </ListItemIcon>
                      <ListItemContent>
                        <ListItemTitle>{activity.title}</ListItemTitle>
                        <ListItemDescription>{activity.description}</ListItemDescription>
                      </ListItemContent>
                      <ListItemMeta>
                        <ListItemTime>{activity.time}</ListItemTime>
                        {activity.status === 'success' && (
                          <ListItemStatus status="success">Completed</ListItemStatus>
                        )}
                      </ListItemMeta>
                    </ListItem>
                  </motion.div>
                ))}
              </AnimatePresence>
            </List>

            <SectionTitle style={{ marginTop: '1.5rem' }}>Weekly Performance</SectionTitle>
            <MmsDashboardRendererWrapper
              dashboarddata={dashboardData}
              viewmode="dashboard"
              colourarray={['#ff0000', '#00ff00']}
            />
            {/* <ChartContainer>
              {systemPerformance.claimsProcessed.map((value, index) => (
                <ChartBar
                  key={`claims-${index}`}
                  height={value}
                  color="#3b82f6"
                  value={value}
                />
              ))}
            </ChartContainer>
            <ChartLegend>
              {systemPerformance.days.map(day => (
                <span key={day}>{day}</span>
              ))}
            </ChartLegend> */}
          </Card>

          <Card>
            <SectionTitle>Component Status</SectionTitle>
            <List>
              {componentStatus.map((component, index) => (
                <ListItem key={index}>
                  <ListItemContent>
                    <ListItemTitle>{component.name}</ListItemTitle>
                    <ProgressContainer>
                      <ProgressBar
                        progress={component.load}
                        color={
                          component.status === 'critical' ? '#ef4444' :
                            component.status === 'degraded' ? '#f59e0b' : '#10b981'
                        }
                      />
                    </ProgressContainer>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <ListItemDescription>Load: {component.load.toFixed(1)}%</ListItemDescription>
                      <ListItemStatus status={
                        component.status === 'critical' ? 'critical' :
                          component.status === 'degraded' ? 'warning' : 'success'
                      }>
                        {component.status === 'critical' ? 'Critical' :
                          component.status === 'degraded' ? 'Degraded' : 'Operational'}
                      </ListItemStatus>
                    </div>
                  </ListItemContent>
                </ListItem>
              ))}
            </List>
          </Card>
        </InfoSection>
      </div>
    </DashboardContainer>
  );
};

export default Dashboard;
